import { type ReactElement } from 'react';
import {
  useFormContext,
  useFormState,
  type FieldError,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { CheckboxField, type CheckboxFieldProps } from '../CheckboxField';

export type RHFCheckboxFieldProps<T extends FieldValues> = Omit<
  CheckboxFieldProps<T>,
  'control' | 'error'
> & {
  /** When set, overrides the error read from form state (e.g. Storybook). */
  error?: FieldError;
};

/**
 * {@link CheckboxField} with `control` (and default `error`) from `useFormContext`.
 * Must be used inside {@link Form}.
 */
export const RHFCheckboxField = <T extends FieldValues>({
  error: errorProp,
  labelElement,
  name,
  onChange,
}: RHFCheckboxFieldProps<T>): ReactElement => {
  const { control } = useFormContext<T>();
  const { errors } = useFormState({ control, name: [name] });
  const formError = errors[name] as FieldError | undefined;
  const error = errorProp ?? formError;

  return (
    <CheckboxField
      control={control}
      name={name}
      labelElement={labelElement}
      error={error}
      onChange={onChange}
    />
  );
};
